/**
 * A CHAT STAYS ON ITS PROJECT (Tom, 2026-10-20: "if we try to switch project
 * I think we should provide a button to automatically create a new chat for
 * the project"). The server refuses moving a chat that already works on a
 * project to another one (VC-housekeeping's guard) with
 * {code: "chat_bound_to_project", next: ["new_chat_for_project"],
 * project_id, project_name, bound_project_id, bound_project_name}; the
 * agent's tool result carries the same object. This reads it.
 *
 * A chat's write to another project's records is refused too (V26, on a
 * branch, on main and on drafts; Tom, 2026-10-23: "Yes, before the merge"):
 * {code: "other_island", reason, failed: [{id, name, type}],
 * next: ["open_island"]}. When the record it names is a project, the card
 * offers the same new chat for it, under the server's own reason.
 */
export type NewChatForProject = {
  /** the project the chat was asked to move to: it gets a chat of its own */
  projectId: string
  projectName: string | null
  /** the project this chat stays on */
  boundProjectName: string | null
  reason: string | null
}

function parsed(content: unknown): Record<string, unknown> | null {
  if (content && typeof content === 'object' && !Array.isArray(content)) return content as Record<string, unknown>
  if (typeof content !== 'string') return null
  try {
    const v = JSON.parse(content)
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null
  } catch {
    return null
  }
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : null)

/** other_island: the project the write belongs to, or null (an island that
 *  is not a project, or one the reader may not read, gets no chat) */
function otherIsland(r: Record<string, unknown>): NewChatForProject | null {
  if (!Array.isArray(r.next) || !r.next.includes('open_island') || !Array.isArray(r.failed)) return null
  const project = r.failed.find((f): f is Record<string, unknown> =>
    !!f && typeof f === 'object' && (f as Record<string, unknown>).type === 'Project' && !!str((f as Record<string, unknown>).id))
  if (!project) return null
  return {
    projectId: project.id as string,
    projectName: str(project.name),
    boundProjectName: null,
    reason: str(r.reason) ?? str(r.error),
  }
}

/** The new-chat action a refused project switch, or a refused write to
 *  another project, offers; or null. */
export function newChatForProject(content: unknown): NewChatForProject | null {
  const outer = parsed(content)
  // a refusal as a request answered it, `{detail: {...}}`, or the object itself
  const r = outer && !outer.code && outer.detail && typeof outer.detail === 'object' && !Array.isArray(outer.detail)
    ? (outer.detail as Record<string, unknown>)
    : outer
  if (r?.code === 'other_island') return otherIsland(r)
  if (!r || r.code !== 'chat_bound_to_project') return null
  if (!Array.isArray(r.next) || !r.next.includes('new_chat_for_project')) return null
  if (typeof r.project_id !== 'string' || !r.project_id) return null
  return {
    projectId: r.project_id,
    projectName: str(r.project_name),
    boundProjectName: str(r.bound_project_name),
    reason: str(r.reason) ?? str(r.error),
  }
}
